import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  alpha,
  useTheme
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { Plan } from '../services/subscription';

interface PlanCardProps {
  plan: Plan;
  selected: boolean;
  onSelect: () => void;
}

const PlanCard: React.FC<PlanCardProps> = ({ plan, selected, onSelect }) => {
  const theme = useTheme();

  const formattedTotal = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(plan.totalPriceBRL);

  const formattedInstallment = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(plan.installmentPriceBRL);

  return (
    <Card
      onClick={onSelect}
      sx={{
        cursor: 'pointer',
        position: 'relative',
        height: '100%',
        border: selected ? `3px solid ${theme.palette.primary.main}` : '1px solid',
        borderColor: selected ? theme.palette.primary.main : 'divider',
        boxShadow: selected ? `0 0 20px ${alpha(theme.palette.primary.main, 0.3)}` : 2,
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: selected 
            ? `0 0 25px ${alpha(theme.palette.primary.main, 0.4)}`
            : 4,
        },
      }}
    >
      {selected && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            bgcolor: 'primary.main',
            color: 'white',
            borderRadius: '50%',
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CheckCircle />
        </Box>
      )}

      <CardContent sx={{ p: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {plan.name}
          </Typography>
          
          {plan.maxUsers && (
            <Chip
              label={`Até ${plan.maxUsers} usuário${plan.maxUsers === 1 ? '' : 's'}`}
              color="primary"
              size="small"
              sx={{ mb: 2 }}
            />
          )}

          <Typography
            variant="h3"
            color="primary"
            fontWeight="bold"
            sx={{ mt: 2 }}
          >
            {formattedInstallment}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            por mês
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            {plan.installments}x no cartão
          </Typography>

          <Typography variant="h6" sx={{ mt: 2 }}>
            Total: {formattedTotal}
          </Typography>
        </Box>

        <List dense sx={{ mt: 2 }}>
          {plan.features.map((feature, index) => (
            <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircle color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={feature}
                primaryTypographyProps={{
                  variant: 'body2',
                  color: 'text.secondary',
                }}
              />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

export default PlanCard;
